import React, { useState, useEffect } from "react";
import GuidelinePassport from "./components/GuidelinePassport";
import CheckQuality from "./components/CheckQuality";
import Verifying from "./components/Verifying";
import TakePhotoError from "./components/TakePhotoError";
import { useLocation } from "react-router-dom";
import InvalidToken from "./components/InvalidToken";
import UploadPhoto from "./components/UploadPhoto";
import PassportReject from "./components/PassportReject";
import { usePassportService } from "./usePassportService";
import { logPassportUploadFailed } from "./passportScanDiagnostics";

const Passport = ({ validation, onComplete, onSessionUpdate }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") || sessionStorage.getItem("ekycSessionToken");

  const { upload, verify } = usePassportService();

  const [step, setStep] = useState("passport-guideline");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [id, setId] = useState(null);

  const [progressStates, setProgressStates] = useState({
    passport: "Not Started",
  });

  const [redirectURL, setRedirectURL] = useState(null);

  const [mrzTextArray, setMrzTextArray] = useState([]);
  const [mrzImageBlob, setMrzImageBlob] = useState(null);
  const [passportOriginalImageBlob, setPassportOriginalImageBlob] =
    useState(null);
  const [passportImageBlob, setPassportImageBlob] = useState(null);
  const [takePhotoErrorMessage, setTakePhotoErrorMessage] = useState("");

  const [userUploadImageBuffer, setUserUploadImageBuffer] = useState(null);

  const [hasPassportCheckCompleted, setHasPassportCheckCompleted] =
    useState(false);

  useEffect(() => {
    if (validation) {
      setProgressStates({
        passport: validation?.ComponentResults?.[validation.CurrentStep.ID]
          ?.Result
          ? validation?.ComponentResults?.[validation.CurrentStep.ID].Result
          : "Not Started",
      });
    } else {
      setStep("invalid-token");
    }
  }, [validation]);

  useEffect(() => {
    if (progressStates.passport === "Pending") {
      setStep("verifying");
    } else if (progressStates.passport === "Not Started") {
      setStep("passport-guideline");
    } else if (progressStates.passport === "Fail") {
      setStep("passport-rejected");
    } else {
      if (onComplete) {
        onComplete({ validationID: validation?.ValidationID });
      }
    }
  }, [progressStates]);

  const renderSteps = () => {
    switch (step) {
      case "passport-guideline":
        return (
          <GuidelinePassport
            onNext={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("passport-upload");
            }}
          />
        );
      case "passport-upload":
        return (
          <UploadPhoto
            imageBuffer={userUploadImageBuffer}
            validationID={validation.ValidationID}
            onSuccess={({
              croppedImageBlob,
              originalImageBlob,
              mrzImageBlob,
              mrzTextArray,
            }) => {
              setPassportImageBlob(croppedImageBlob);
              setPassportOriginalImageBlob(originalImageBlob);
              setMrzImageBlob(mrzImageBlob);
              setMrzTextArray(mrzTextArray);
              setStep("passport-check-quality");
            }}
            onError={(error, showImage = true) => {
              setTakePhotoErrorMessage({ text: error, showImage });
              setStep("passport-upload-error");
            }}
          />
        );
      case "passport-upload-error":
        return (
          <TakePhotoError
            errorMessage={takePhotoErrorMessage.text}
            showImage={takePhotoErrorMessage.showImage}
            imageBuffer={userUploadImageBuffer}
            onRetry={() => {
              setTakePhotoErrorMessage("");
              setStep("passport-guideline");
            }}
          />
        );
      case "passport-check-quality":
        return (
          <CheckQuality
            isSubmitting={isSubmitting}
            imageBlob={passportImageBlob}
            onRetake={(imageBuffer) => {
              setUserUploadImageBuffer(imageBuffer);
              setStep("passport-upload");
            }}
            onConfirm={() => {
              setIsSubmitting(true);
              setHasPassportCheckCompleted(false);

              const formData = new FormData();
              formData.append("validationID", validation.ValidationID);
              formData.append("croppedImage", passportImageBlob);
              formData.append("originalImage", passportOriginalImageBlob);
              formData.append("mrzImage", mrzImageBlob);
              formData.append("mrzText", JSON.stringify(mrzTextArray));

              upload(formData)
                .then((res) => {
                  onSessionUpdate?.(res.session);
                  if (res.status === "success") {
                    verify({ validationID: validation.ValidationID }).then(
                      (verifyRes) => {
                        onSessionUpdate?.(verifyRes.session);
                        setHasPassportCheckCompleted(true);
                      }
                    );
                    setStep("verifying");
                  } else {
                    logPassportUploadFailed(res.httpStatus, validation.ValidationID);
                  }

                  setIsSubmitting(false);
                })
                .catch((error) => {
                  logPassportUploadFailed(error?.httpStatus, validation.ValidationID);
                  setIsSubmitting(false);
                });
            }}
          />
        );
      case "passport-rejected":
        return (
          <PassportReject
            validationID={validation.ValidationID}
            onRetry={() => {
              setStep("passport-guideline");
            }}
          />
        );
      case "verifying":
        return (
          <Verifying
            validationID={validation.ValidationID}
            progressStates={progressStates}
            hasPassportCheckCompleted={hasPassportCheckCompleted}
            onSessionUpdate={onSessionUpdate}
            onPass={(checkResult) => {
              setProgressStates((prev) => ({ ...prev, passport: "Pass" }));
              onComplete({ validationID: validation.ValidationID });
            }}
            onFail={(checkResult) => {
              if (checkResult.validationCount > 3) {
                return setStep("exceed-max-retry");
              }
              setProgressStates((prev) => ({ ...prev, passport: "Fail" }));
              setStep("passport-rejected");
            }}
          />
        );
      case "invalid-token":
        return <InvalidToken />;
      default:
        return <div>Loading...</div>;
    }
  };

  return (
    <div className="h-auto w-full bg-gray-50 flex justify-center items-center">
      <div className="h-full w-full max-w-md bg-black relative md:rounded-xl md:shadow-lg">
        {!token ? <InvalidToken /> : renderSteps()}
      </div>
    </div>
  );
};

export default Passport;
